/**
 * Hook for idle timeout detection and auto-logout
 * Requirements: 6.6 - Auto logout after inactivity (30 minutes)
 */
import { useEffect, useRef, useCallback } from 'react';

const DEFAULT_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'] as const;

export interface UseIdleTimeoutOptions {
  onIdle: () => void;
  timeout?: number;
  isEnabled?: boolean;
}

export interface UseIdleTimeoutReturn {
  resetTimer: () => void;
}

export const useIdleTimeout = ({
  onIdle,
  timeout = DEFAULT_TIMEOUT,
  isEnabled = true,
}: UseIdleTimeoutOptions): UseIdleTimeoutReturn => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onIdleRef = useRef(onIdle);

  // Keep callback ref updated
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    clearTimer();
    if (isEnabled) {
      timeoutRef.current = setTimeout(() => {
        onIdleRef.current();
      }, timeout);
    }
  }, [clearTimer, timeout, isEnabled]);

  useEffect(() => {
    if (!isEnabled) {
      clearTimer();
      return;
    }

    // Start initial timer
    resetTimer();

    // Handle activity events
    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Cleanup
    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isEnabled, resetTimer, clearTimer]);

  return { resetTimer };
};
